import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import {
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
} from '@unified-checkout/design-system';
import {
  Danger,
  SuccessHeader,
} from '@unified-checkout/design-system/icons/state_icons';

import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';

import { MODAL_STEP_STATUS } from '@app/dashboard/constants/transactions';
import { EMPTY_VALUE, OTP_LENGTH } from '@app/dashboard/constants/shared';
import { StatusContent } from '@app/dashboard/components/inner_layout/StatusContent';
import type { TState } from '@app/dashboard/types/innerLayout/status-content';
import type { IOtpRefundData } from '@app/dashboard/types/innerLayout/transactions/modals/otp';

import {
  captureFieldValidations,
  getMaxCapturedAmount,
  maxRefundAmount,
  refundFieldValidations,
} from '../utils';

import { OTPModalBody } from '../modals/OTPModalBody';
import { RefundModalBody } from '../modals/RefundModalBody';
import { CaptureModalBody } from '../modals//CaptureModalBody';

export const RenderCaptureModalContent = ({
  selectedTransaction,
  captureAmountField,
  setCaptureAmountField,
  lang,
  amount,
  currency,
  success,
  createdAt,
  pending,
  paymentMethod
}: {
  selectedTransaction: any;
  captureAmountField: {
    value: string;
    error?: string;
  };
  setCaptureAmountField: Dispatch<
    SetStateAction<{
      value: string;
      error: string;
    }>
  >;
  lang: 'en' | 'ar';
  amount: number;
  currency: string;
  success: boolean;
  createdAt: string | any;
  pending: boolean;
  paymentMethod: string;
}) => {
  const { t } = useTranslation();

  const handleCaptureAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const errorMsg = captureFieldValidations(value, selectedTransaction) || '';
    setCaptureAmountField({ value, error: errorMsg });
  };

  return (
    <CaptureModalBody
      localization={{
        maxCaptureAmount: t(
          'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.maxAmount'
        ),
      }}
      detailsList={[
        {
          label: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.amount'
          ),
          value: (
            <>
              {FormatCurrencyForDisplay(currency || EMPTY_VALUE)} {amount}
            </>
          ),
        },
        {
          label: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.transSuccess'
          ),
          value: success
            ? t(
                'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.true'
              )
            : t(
                'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.false'
              ),
        },
        {
          label: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.createdAt'
          ),
          value: createdAt && formatToLocalizedDateTime(createdAt, lang),
        },
        {
          label: t(
            'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.transPending'
          ),
          value: pending
            ? t(
                'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.true'
              )
            : t(
                'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.false'
              ),
        },
      ]}
      maxCaptureAmount={
        <>
          {FormatCurrencyForDisplay(currency)}{' '}
          {getMaxCapturedAmount(selectedTransaction)}
        </>
      }
      captureAmountInput={{
        name: 'capture_amount',
        label: t(
          'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.amount'
        ),
        placeholder: t(
          'innerLayout.transactionsPage.singleTransactionsPage.modals.capture.placeholder.captureAmount'
        ),
        value: captureAmountField.value,
        error: captureAmountField.error,
        onChangeHandler: handleCaptureAmountChange,
      }}
      paymentMethod={paymentMethod}
    />
  );
};

export const RenderOTPModalBody = ({
  otpRefundData,
  setOtpRefundData,
}: {
  otpRefundData: IOtpRefundData;
  setOtpRefundData: Dispatch<SetStateAction<IOtpRefundData | undefined>>;
}) => {
  const { t } = useTranslation();

  return (
    <OTPModalBody
      length={OTP_LENGTH}
      onChangeHandler={(otp) =>
        setOtpRefundData((prev: IOtpRefundData | undefined) => ({
          ...prev,
          otp,
          error: '',
        }))
      }
      localization={{
        resendText: t('form.resendCodeIn'),
        resendBtnText: t('form.resend'),
      }}
      error={otpRefundData?.error}
    />
  );
};

export const RenderRefundModalContent = ({
  modalStep,
  lang,
  amount,
  currency,
  selectedTransaction,
  refundType,
  toBeRefundedProducts,
  canPartialRefund,
  isPartialRefund,
  partialAmount,
  is_refund_pin_required,
  refundPin,
  caf_detail,
  source_data,
  setProductsRefund,
  setIsPartialRefund,
  setPartialAmount,
  setRefundPin,
  paymentMethod
}: {
  modalStep: { type: string; message?: string };
  lang: 'ar' | 'en';
  amount?: number;
  currency: string;
  selectedTransaction: any;
  refundType: any;
  toBeRefundedProducts: any[];
  setProductsRefund: (data: { ids: string; price: number }) => void;
  canPartialRefund: boolean;
  isPartialRefund: boolean;
  partialAmount: {
    value: string;
    error: string;
  };
  is_refund_pin_required: boolean;
  refundPin: {
    value: string | undefined;
    error: string;
  };
  caf_detail: any;
  source_data: any;
  paymentMethod: string;
  setIsPartialRefund: (value: boolean) => void;
  setPartialAmount: Dispatch<
    SetStateAction<{
      value: string;
      error: string;
    }>
  >;
  setRefundPin: Dispatch<
    SetStateAction<{
      value: string | undefined;
      error: string;
    }>
  >;
}) => {
  const { t } = useTranslation();

  const handlePartialAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const errorMsg = refundFieldValidations(value, selectedTransaction) || '';
    setPartialAmount({ value, error: errorMsg });
  };

  const handlePinChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const errorMsg = value ? '' : t('errors.required');
    setRefundPin({ error: errorMsg, value });
  };

  switch (modalStep.type) {
    case MODAL_STEP_STATUS.START:
      return (
        <RefundModalBody
          lang={lang}
          totalAmount={amount && formatAmountWithCommas({ amount, currency})}
          refundableAmount={formatAmountWithCommas({
            amount: maxRefundAmount(selectedTransaction),
            currency,
          })}
          currency={FormatCurrencyForDisplay(currency) as any}
          refundType={
            refundType /* productRefundPaymentTypes.includes(source_data.type) ? 'products' : 'transaction' */
          }
          products={toBeRefundedProducts}
          toBeRefundedProducts={(ids, price) => {
            setProductsRefund({ price, ids: ids.toString() });
          }}
          canPartiallyRefunded={canPartialRefund}
          isDefaultPartialRefund={isPartialRefund}
          localization={{
            quantity: t('innerLayout.quantity'),
            totalAmount: t(
              'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.totalAmount'
            ),
            refundableAmount: t(
              'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.refundableAmount'
            ),
            refundFullAmount: t(
              'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.refundFullAmount'
            ),
          }}
          onRefundInputToggle={(visible) => {
            setIsPartialRefund(visible);
          }}
          partialRefundInput={{
            name: 'refund_amount',
            label: t(
              'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.amountToRefund'
            ),
            placeholder: t(
              'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.placeholders.refund'
            ),
            value: String(partialAmount.value),
            error: partialAmount.error,
            leadingText: FormatCurrencyForDisplay(currency) as any,
            onChangeHandler: handlePartialAmountChange,
          }}
          {...(is_refund_pin_required
            ? {
                additionalInput: {
                  name: 'pin',
                  label: t(
                    'innerLayout.transactionsPage.singleTransactionsPage.modals.refund.pin'
                  ),
                  value: String(refundPin.value || ''),
                  error: refundPin.error,
                  onChangeHandler: handlePinChange,
                },
              }
            : {})}
          cafDetails={caf_detail}
          isInstantRefund={!!source_data?.instant_refund}
          paymentMethod={paymentMethod}
        />
      );
    case MODAL_STEP_STATUS.SUCCESS:
    case MODAL_STEP_STATUS.FAILED:
      return (
        <StatusContent
          state={modalStep.type as TState}
          title={t(
            `innerLayout.transactionsPage.singleTransactionsPage.modals.refund.${modalStep.type}`
          )}
          supportingText={modalStep.message}
          statusIcon={
            modalStep.type === MODAL_STEP_STATUS.SUCCESS
              ? SuccessHeader
              : Danger
          }
        />
      );
    default:
      return null;
  }
};
